import React, { useState } from 'react';
import './CertificateGenerator.css';

function CertificateGenerator({ user, onUserUpdate, onGoToWallet }) {
  const [generating, setGenerating] = useState(false);
  const [registering, setRegistering] = useState(false);
  // ++ Згода на показ приватних полів у сертифікаті
  const PRIVATE_OPTIONS = [
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'telegram', label: 'Telegram' },
    { key: 'address', label: 'Address' },
    { key: 'dateOfBirth', label: 'Date of Birth' },
  ];
  const [publicFields, setPublicFields] = useState(user?.certificatePublicFields || []);
  const togglePublic = (k) =>
    setPublicFields((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const filledPrivate = PRIVATE_OPTIONS.filter((o) => String(user?.[o.key] || '').trim());
  const hasCertificate = user?.lastCertificateUrl && user?.lastCertificateDate;
  const hasBlockchain = user?.blockchainTxHash;
  const hasWallet = !!(user?.solanaAddress || user?.walletAddress);
  const certificate = hasCertificate ? {
    url: user.lastCertificateUrl,
    fileName: user.lastCertificateFileName || `Genesis-Certificate-${user.genesisID}.pdf`,
    generatedAt: new Date(user.lastCertificateDate).toLocaleString(),
    hasPhoto: user.lastCertificateHasPhoto || false
  } : null;

  const generateCertificate = async () => {
    if (!user?.genesisID) {
      alert('User information not available');
      return;
    }

    setGenerating(true);

    try {
           // ++ Спершу зберігаємо згоду (сервер бере її лише з профілю)
      const consentRes = await fetch('https://orkxz4jpah.execute-api.us-east-1.amazonaws.com/update-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ genesisID: user.genesisID, certificatePublicFields: publicFields })
      });
      const consentJson = await consentRes.json();
      if (!consentJson.success) throw new Error(consentJson.error || 'Could not save privacy settings');

      const response = await fetch('https://j3g4jcz78i.execute-api.us-east-1.amazonaws.com/prod/generate-certificate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ genesisID: user.genesisID })
      });

      const result = await response.json();

      if (result.success) {
        const updatedUser = {
          ...user,
          lastCertificateUrl: result.certificate.url,
          lastCertificateDate: result.certificate.generatedAt,
          lastCertificateFileName: result.certificate.fileName,
          lastCertificateHasPhoto: result.certificate.hasPhoto
          , certificatePublicFields: publicFields
        };
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        if (onUserUpdate) onUserUpdate(updatedUser);
        alert('Certificate generated successfully!');
      } else {
        alert('Failed to generate certificate: ' + result.error);
      }
    } catch (error) {
      alert('Network error: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  // ++ НОВА ФУНКЦІЯ: реєстрація в блокчейні
  const registerOnBlockchain = async () => {
    if (!user?.solanaAddress) {
      alert('Please create your TheoWallet first (TheoWallet tab).');
      return;
    }

    setRegistering(true);

    try {
      const response = await fetch('https://j3g4jcz78i.execute-api.us-east-1.amazonaws.com/prod/blockchain-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ genesisID: user.genesisID })
      });

      const result = await response.json();

      if (result.success) {
        if (result.alreadyRegistered) {
          alert('Already registered on blockchain!\nTx: ' + result.blockchain.txHash);
        } else {
          // Update user data with blockchain info
          const updatedUser = {
            ...user,
            blockchainTxHash: result.blockchain.txHash,
            blockchainDocHash: result.blockchain.docHash,
            blockchainTimestamp: result.blockchain.timestamp
          };
          localStorage.setItem('currentUser', JSON.stringify(updatedUser));
          if (onUserUpdate) onUserUpdate(updatedUser);
          alert('Successfully registered on Solana blockchain!');
        }
      } else {
        alert('Blockchain registration failed: ' + result.error);
      }
    } catch (error) {
      alert('Network error: ' + error.message);
    } finally {
      setRegistering(false);
    }
  };
  const privacyBlock = filledPrivate.length > 0 && (
    <div className="cert-privacy">
      <p className="cert-privacy-title">Show on certificate (optional)</p>
      {filledPrivate.map((o) => (
        <label key={o.key} className="cert-privacy-item">
          <input type="checkbox" checked={publicFields.includes(o.key)} onChange={() => togglePublic(o.key)} />
          {o.label}
        </label>
      ))}
      <small className="cert-privacy-note">Unchecked fields stay private.</small>
    </div>
  );
  const viewCertificate = () => { if (certificate) window.open(certificate.url, '_blank'); };

  const downloadCertificate = () => {
    if (certificate) {
      const link = document.createElement('a');
      link.href = certificate.url;
      link.download = certificate.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const shareCertificate = async () => {
    if (!certificate) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Genesis Certificate',
          text: `Genesis Certificate for ${user.firstName} ${user.lastName}`,
          url: certificate.url
        });
      } catch (error) { fallbackShare(); }
    } else { fallbackShare(); }
  };

  const fallbackShare = () => {
    navigator.clipboard.writeText(certificate.url).then(() => {
      alert('Certificate link copied to clipboard!');
    }).catch(() => { prompt('Copy this link to share:', certificate.url); });
  };

  return (
    <div className="certificate-generator-mobile">
      <div className="certificate-header">
        <h3>Genesis Certificate</h3>
      </div>

      {!certificate ? (
        <div className="generate-section">
          <p className="description">
            Generate your official Genesis Certificate with verified information
          </p>
          {hasWallet && privacyBlock}
          {hasWallet ? (
            <button
              className="btn-generate-mobile"
              onClick={generateCertificate}
              disabled={generating}
            >
              {generating ? <><span className="spinner"></span>Generating...</> : 'Generate Certificate'}
            </button>
          ) : (
            <>
              <p className="description" style={{ color: '#7a4a00' }}>
                🔑 Your certificate is linked to your TheoWallet public key. Create your TheoWallet first.
              </p>
              <button className="btn-generate-mobile" onClick={onGoToWallet}>
                Create TheoWallet
              </button>
            </>
          )}

          <div className="blockchain-notice">
            <span className="blockchain-icon">⛓️</span>
            Blockchain verification — generate certificate first
          </div>
        </div>
      ) : (
        <div className="certificate-management">
          <div className="certificate-status">
            <div className="status-icon">📄</div>
            <div className="status-info">
              <p className="status-title">Certificate Ready</p>
              <p className="status-date">Generated: {certificate.generatedAt}</p>
            </div>
          </div>

          {/* ++ НОВЕ: блокчейн блок */}
          {hasBlockchain ? (
            <div className="blockchain-verified">
              <span className="blockchain-icon">✅</span>
              <div className="blockchain-info">
                <p className="blockchain-title">Registered on Solana</p>
                <a
                  href={`https://explorer.solana.com/tx/${user.blockchainTxHash}`}
                  target="_blank"
                  rel="noreferrer"
                  className="blockchain-link"
                >
                  View on Explorer →
                </a>
              </div>
            </div>
          ) : (
            <button
              className="btn-blockchain"
              onClick={registerOnBlockchain}
              disabled={registering}
            >
              {registering ? (
                <><span className="spinner"></span>Registering...</>
              ) : (
                <><span className="blockchain-icon">⛓️</span>Register on Blockchain</>
              )}
            </button>
          )}

          <div className="certificate-actions-mobile">
            <button className="btn-action primary" onClick={downloadCertificate}>
              <span className="btn-icon">📄</span>Open PDF
            </button>
            <button className="btn-action share" onClick={shareCertificate}>
              <span className="btn-icon">📤</span>Share
            </button>
          </div>

          <div className="regenerate-section">
            {privacyBlock}
            <button className="btn-regenerate" onClick={generateCertificate} disabled={generating}>
              {generating ? 'Generating...' : 'Generate New'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CertificateGenerator;
